import { useMemo, useState } from "react";
import { categories, feedList } from "@/mocks/events";
import { Reveal, SectionHead } from "./ui";

export default function Feed() {
  const [active, setActive] = useState("全部");
  const [followed, setFollowed] = useState<Set<string>>(new Set());

  const list = useMemo(
    () => (active === "全部" ? feedList : feedList.filter((f) => f.category === active)),
    [active]
  );

  const toggleFollow = (id: string) => {
    setFollowed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <section id="feed" className="scroll-mt-20 bg-background-100/60 py-16 md:py-24">
      <div className="mx-auto max-w-[1320px] px-4 md:px-6">
        <SectionHead
          index="04"
          en="Breaking Feed"
          title="最新爆料流"
          desc="按时间倒序滚动播报的最新线索与各方动态，每条都带分类、信源与热度，可一键切换栏目，随时追更你在意的瓜。"
        />

        {/* category filter */}
        <div className="mb-7 flex flex-wrap gap-2">
          {categories.map((c) => {
            const isActive = active === c;
            return (
              <button
                key={c}
                type="button"
                onClick={() => setActive(c)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 text-foreground-600 ring-1 ring-background-200 hover:bg-background-200 hover:text-foreground-900"
                }`}
              >
                {c}
                {c !== "全部" && (
                  <span
                    className={`ml-1.5 text-xs ${
                      isActive ? "text-background-100/80" : "text-secondary-400"
                    }`}
                  >
                    {feedList.filter((f) => f.category === c).length}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* feed rows */}
        <Reveal>
          <div className="overflow-hidden rounded-2xl border border-background-200 bg-background-50">
            {list.map((f, i) => {
              const isFollow = followed.has(f.id);
              return (
                <div
                  key={f.id}
                  className={`group flex flex-col gap-3 px-5 py-4 transition-colors hover:bg-background-100/60 md:flex-row md:items-start md:gap-5 md:px-6 ${
                    i !== 0 ? "border-t border-background-200" : ""
                  }`}
                >
                  <span className="hidden w-6 shrink-0 items-center justify-center font-heading text-lg font-black text-secondary-300 md:flex">
                    {String(i + 1).padStart(2, "0")}
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="whitespace-nowrap rounded-sm bg-accent-100 px-2 py-0.5 text-xs font-bold text-accent-800">
                        {f.category}
                      </span>
                      <span className="text-[11px] text-secondary-400">信源 · {f.source}</span>
                      <span className="text-[11px] text-secondary-400">·</span>
                      <span className="flex items-center gap-1 text-[11px] text-secondary-400">
                        <i className="ri-time-line" aria-hidden="true"></i>
                        {f.time}
                      </span>
                    </div>
                    <h3 className="mt-1.5 font-heading text-[15px] font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-base">
                      {f.title}
                    </h3>
                    <p className="mt-1.5 hidden max-w-3xl text-sm leading-6 text-foreground-500 sm:block">
                      {f.digest}
                    </p>
                  </div>

                  <div className="flex shrink-0 items-center gap-3">
                    <span className="flex items-center gap-1 text-xs text-secondary-500">
                      <i className="ri-fire-line text-accent-600" aria-hidden="true"></i>
                      {f.heat.toLocaleString()}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleFollow(f.id)}
                      aria-pressed={isFollow}
                      className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                        isFollow
                          ? "bg-primary-500 text-background-50"
                          : "bg-secondary-100 text-secondary-700 hover:bg-secondary-200"
                      }`}
                    >
                      <i className={isFollow ? "ri-check-double-line" : "ri-add-line"} aria-hidden="true"></i>
                      {isFollow ? "已追更" : "追更"}
                    </button>
                  </div>
                </div>
              );
            })}

            {list.length === 0 && (
              <p className="px-6 py-14 text-center text-sm text-secondary-500">
                该栏目暂无新增动态，去别的分区逛逛吧。
              </p>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}